import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  ArrowLeft,
  Building2,
  Users,
  CreditCard,
  Save,
  Loader2,
  UserPlus,
  ShieldCheck,
  ShieldOff,
  X,
  Check,
} from "lucide-react";
import { useToast } from "../../lib/toast";

interface TenantData {
  id: string;
  name: string;
  rnc: string;
  business_type: string;
  industry: string;
  employee_count: number;
  contact_email: string;
  contact_phone: string;
  address: string;
  city: string;
  subscription_tier: string;
  monthly_rate: number;
  is_active: boolean;
  billing_status: string;
  stripe_customer_id: string;
  last_payment_date: string;
  created_at: string;
}

interface TenantUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  permissions: string[];
  is_active: boolean;
  created_at: string;
}

interface TenantPayment {
  id: string;
  amount: number;
  currency: string;
  stripe_payment_id: string;
  status: string;
  period_start: string;
  period_end: string;
  created_at: string;
}

const ROLES = ["admin", "manager", "cashier", "user"];
const PERMISSIONS = [
  "invoices",
  "inventory",
  "reports",
  "customers",
  "pos",
  "taxes",
  "reconciliation",
  "settings",
];

export const TenantDetail: React.FC = () => {
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<"info" | "users" | "payments">("info");
  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [payments, setPayments] = useState<TenantPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState<Partial<TenantData>>({});

  // User creation modal
  const [showUserModal, setShowUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    full_name: "",
    role: "user",
    permissions: [] as string[],
  });
  const [savingUser, setSavingUser] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [tRes, uRes, pRes] = await Promise.all([
        supabase.from("tenants").select("*").eq("id", id).single(),
        supabase
          .from("tenant_users")
          .select("*")
          .eq("tenant_id", id)
          .order("created_at", { ascending: false }),
        supabase
          .from("tenant_payments")
          .select("*")
          .eq("tenant_id", id)
          .order("created_at", { ascending: false }),
      ]);
      if (tRes.error) throw tRes.error;
      setTenant(tRes.data as TenantData);
      setEditForm(tRes.data as TenantData);
      setUsers((uRes.data || []) as TenantUser[]);
      setPayments((pRes.data || []) as TenantPayment[]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, setTenant, setEditForm, setUsers, setPayments]);

  useEffect(() => {
    fetchAll();
  }, [id, fetchAll]);

  const handleSaveTenant = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("tenants")
        .update({
          name: editForm.name,
          rnc: editForm.rnc,
          business_type: editForm.business_type,
          industry: editForm.industry,
          employee_count: editForm.employee_count,
          contact_email: editForm.contact_email,
          contact_phone: editForm.contact_phone,
          address: editForm.address,
          city: editForm.city,
          subscription_tier: editForm.subscription_tier,
          monthly_rate: editForm.monthly_rate,
          is_active: editForm.is_active,
          billing_status: editForm.billing_status,
        })
        .eq("id", id);
      if (error) throw error;
      fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error al guardar", "No se pudo guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingUser(true);
    try {
      const { error } = await supabase.from("tenant_users").insert({
        tenant_id: id,
        email: newUser.email,
        full_name: newUser.full_name,
        role: newUser.role,
        permissions: newUser.permissions,
        is_active: true,
      });
      if (error) throw error;
      setShowUserModal(false);
      setNewUser({ email: "", full_name: "", role: "user", permissions: [] });
      fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error", "No se pudo crear el usuario");
    } finally {
      setSavingUser(false);
    }
  };

  const toggleUserActive = async (userId: string, currentStatus: boolean) => {
    const { error } = await supabase
      .from("tenant_users")
      .update({ is_active: !currentStatus })
      .eq("id", userId);
    if (!error) fetchAll();
  };

  if (loading) {
    return (
      <div className="flex-1 p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="flex-1 p-8 text-center text-gray-500">
        Comercio no encontrado.
      </div>
    );
  }

  return (
    <div className="flex-1 p-8 overflow-y-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/tenants"
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-4"
        >
          <ArrowLeft size={16} /> Volver al Directorio
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-brand-100 border border-brand-200 flex items-center justify-center">
              <Building2 size={28} className="text-gray-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {tenant.name}
              </h1>
              <p className="text-sm text-gray-500 font-mono">
                RNC: {tenant.rnc} • {tenant.city}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider ${
                tenant.billing_status === "active"
                  ? "bg-brand-50 text-gray-700 border border-brand-200"
                  : tenant.billing_status === "overdue"
                    ? "bg-brand-50 text-gray-700 border border-brand-200"
                    : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              {tenant.billing_status === "active"
                ? "Al Día"
                : tenant.billing_status === "overdue"
                  ? "Moroso"
                  : "Suspendido"}
            </span>
            <span
              className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider ${
                tenant.is_active
                  ? "bg-gray-900 text-white"
                  : "bg-brand-100 text-gray-500"
              }`}
            >
              {tenant.is_active ? "Activo" : "Inactivo"}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-brand-200 mb-8">
        <button
          onClick={() => setTab("info")}
          className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors ${tab === "info" ? "border-gray-900 text-gray-900" : "border-transparent text-gray-400"}`}
        >
          <Building2 size={16} /> Información General
        </button>
        <button
          onClick={() => setTab("users")}
          className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors ${tab === "users" ? "border-gray-900 text-gray-900" : "border-transparent text-gray-400"}`}
        >
          <Users size={16} /> Usuarios ({users.length})
        </button>
        <button
          onClick={() => setTab("payments")}
          className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors ${tab === "payments" ? "border-gray-900 text-gray-900" : "border-transparent text-gray-400"}`}
        >
          <CreditCard size={16} /> Historial de Pagos ({payments.length})
        </button>
      </div>

      {/* Tab: Info */}
      {tab === "info" && (
        <div className="bg-white rounded-2xl border border-brand-100 shadow-sm p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Nombre del Comercio
              </label>
              <input
                value={editForm.name || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                RNC
              </label>
              <input
                value={editForm.rnc || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, rnc: e.target.value })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Tipo de Negocio
              </label>
              <select
                value={editForm.business_type || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, business_type: e.target.value })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              >
                <option value="">Seleccionar...</option>
                <option>Retail</option>
                <option>Restaurante</option>
                <option>Distribuidora</option>
                <option>Farmacia</option>
                <option>Ferretería</option>
                <option>Clínica</option>
                <option>Consultora</option>
                <option>Manufactura</option>
                <option>Servicios Profesionales</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Industria
              </label>
              <select
                value={editForm.industry || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, industry: e.target.value })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              >
                <option value="">Seleccionar...</option>
                <option>Alimentos & Bebidas</option>
                <option>Tecnología</option>
                <option>Salud & Farmacéutica</option>
                <option>Construcción</option>
                <option>Comercio General</option>
                <option>Educación</option>
                <option>Logística & Transporte</option>
                <option>Agricultura</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Cantidad de Empleados
              </label>
              <input
                type="number"
                value={editForm.employee_count || 0}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    employee_count: Number(e.target.value),
                  })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Email de Contacto
              </label>
              <input
                type="email"
                value={editForm.contact_email || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, contact_email: e.target.value })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Teléfono
              </label>
              <input
                value={editForm.contact_phone || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, contact_phone: e.target.value })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Ciudad
              </label>
              <input
                value={editForm.city || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, city: e.target.value })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Dirección
              </label>
              <input
                value={editForm.address || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, address: e.target.value })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              />
            </div>

            <div className="md:col-span-2 border-t border-brand-100 pt-6 mt-2">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">
                Plan & Facturación
              </h3>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Plan Contratado
              </label>
              <select
                value={editForm.subscription_tier || ""}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    subscription_tier: e.target.value,
                  })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              >
                <option>OsirisBrain Business</option>
                <option>OsirisBrain Pro</option>
                <option>OsirisBrain Enterprise</option>
                <option>OsirisBrain Custom</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Tarifa Mensual (USD)
              </label>
              <input
                type="number"
                step="0.01"
                value={editForm.monthly_rate || 0}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    monthly_rate: Number(e.target.value),
                  })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Estado de Cuenta
              </label>
              <select
                value={editForm.billing_status || "active"}
                onChange={(e) =>
                  setEditForm({ ...editForm, billing_status: e.target.value })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              >
                <option value="active">Al Día</option>
                <option value="overdue">Moroso</option>
                <option value="suspended">Suspendido</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Estado del Servicio
              </label>
              <select
                value={editForm.is_active ? "true" : "false"}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    is_active: e.target.value === "true",
                  })
                }
                className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-gray-900"
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo (Suspendido)</option>
              </select>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-brand-100 flex justify-end">
            <button
              onClick={handleSaveTenant}
              disabled={saving}
              className="px-6 py-3 bg-gray-900 hover:bg-gray-950 text-white font-bold rounded-xl text-sm flex items-center gap-2 transition-all disabled:opacity-50 shadow-md active:scale-95"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save size={16} />
              )}{" "}
              Guardar Cambios
            </button>
          </div>
        </div>
      )}

      {/* Tab: Users */}
      {tab === "users" && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={() => setShowUserModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-950 text-white font-bold rounded-xl text-sm transition-all shadow-md active:scale-95"
            >
              <UserPlus size={16} /> Nuevo Usuario
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-brand-50 text-gray-500 border-b border-brand-100 uppercase tracking-wider text-xs">
                <tr>
                  <th className="px-6 py-4 font-bold">Nombre</th>
                  <th className="px-6 py-4 font-bold">Email</th>
                  <th className="px-6 py-4 font-bold">Rol</th>
                  <th className="px-6 py-4 font-bold">Permisos</th>
                  <th className="px-6 py-4 font-bold text-right">Estado</th>
                  <th className="px-6 py-4 font-bold text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="hover:bg-brand-100 transition-colors"
                  >
                    <td className="px-6 py-4 font-normal text-gray-900">
                      {u.full_name}
                    </td>
                    <td className="px-6 py-4 text-brand-500 font-mono text-xs">
                      {u.email}
                    </td>
                    <td className="px-6 py-4">
                      <span className="bg-brand-100 text-gray-700 px-2.5 py-1 rounded-md text-xs font-bold uppercase">
                        {u.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(u.permissions || []).map((p, i) => (
                          <span
                            key={i}
                            className="bg-brand-50 text-gray-500 border border-brand-200 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase"
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium uppercase ${u.is_active ? "bg-brand-50 text-gray-700" : "bg-red-50 text-red-600"}`}
                      >
                        {u.is_active ? (
                          <>
                            <Check size={10} /> Activo
                          </>
                        ) : (
                          <>
                            <X size={10} /> Inactivo
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => toggleUserActive(u.id, u.is_active)}
                        className={`p-2 rounded-lg transition-colors ${u.is_active ? "text-red-500 hover:bg-red-50" : "text-brand-500 hover:bg-brand-100"}`}
                        title={u.is_active ? "Desactivar" : "Activar"}
                      >
                        {u.is_active ? (
                          <ShieldOff size={16} />
                        ) : (
                          <ShieldCheck size={16} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* New User Modal */}
          {showUserModal && (
            <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden border border-brand-100">
                <div className="flex justify-between items-center p-6 border-b border-brand-100 bg-brand-50/50">
                  <h2 className="text-xl font-bold text-gray-900">
                    Nuevo Usuario para {tenant.name}
                  </h2>
                  <button
                    onClick={() => setShowUserModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-900 hover:bg-brand-100 rounded-full transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
                <form onSubmit={handleCreateUser} className="p-6 space-y-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Nombre Completo
                    </label>
                    <input
                      required
                      value={newUser.full_name}
                      onChange={(e) =>
                        setNewUser({ ...newUser, full_name: e.target.value })
                      }
                      className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500"
                      placeholder="Ej: Juan Pérez"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={newUser.email}
                      onChange={(e) =>
                        setNewUser({ ...newUser, email: e.target.value })
                      }
                      className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500"
                      placeholder="usuario@empresa.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Rol
                    </label>
                    <select
                      value={newUser.role}
                      onChange={(e) =>
                        setNewUser({ ...newUser, role: e.target.value })
                      }
                      className="w-full bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-brand-500"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Permisos
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {PERMISSIONS.map((p) => (
                        <label
                          key={p}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase cursor-pointer transition-colors border ${newUser.permissions.includes(p) ? "bg-gray-900 text-white border-gray-900" : "bg-brand-50 text-gray-500 border-brand-200 hover:border-gray-200"}`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={newUser.permissions.includes(p)}
                            onChange={(e) => {
                              if (e.target.checked)
                                setNewUser({
                                  ...newUser,
                                  permissions: [...newUser.permissions, p],
                                });
                              else
                                setNewUser({
                                  ...newUser,
                                  permissions: newUser.permissions.filter(
                                    (x) => x !== p,
                                  ),
                                });
                            }}
                          />
                          {p}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="pt-4 border-t border-brand-100 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowUserModal(false)}
                      className="px-5 py-2.5 rounded-xl font-bold text-brand-500 hover:bg-brand-100 transition-colors text-sm"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={savingUser}
                      className="px-5 py-2.5 rounded-xl font-bold bg-gray-900 text-white hover:bg-gray-950 transition-colors text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                      {savingUser ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Registrar Usuario"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Payments */}
      {tab === "payments" && (
        <div className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-brand-100 bg-brand-50/50 flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <CreditCard size={18} /> Historial de Pagos (Stripe)
            </h3>
            <span className="text-sm font-bold text-gray-500">
              Tarifa:{" "}
              <span className="text-gray-900">${tenant.monthly_rate}/mes</span>
            </span>
          </div>
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-50 text-gray-500 border-b border-brand-100 uppercase tracking-wider text-xs">
              <tr>
                <th className="px-6 py-4 font-bold">ID Stripe</th>
                <th className="px-6 py-4 font-bold">Período</th>
                <th className="px-6 py-4 font-bold text-right">Monto</th>
                <th className="px-6 py-4 font-bold text-right">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {payments.map((p) => (
                <tr key={p.id} className="hover:bg-brand-100 transition-colors">
                  <td className="px-6 py-4 font-mono text-xs text-gray-500">
                    {p.stripe_payment_id}
                  </td>
                  <td className="px-6 py-4 text-gray-700 font-medium">
                    {new Date(p.period_start).toLocaleDateString()} —{" "}
                    {new Date(p.period_end).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right font-normal text-gray-900">
                    ${p.amount} {p.currency}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span
                      className={`px-2.5 py-1 rounded-md text-[10px] font-medium uppercase ${
                        p.status === "paid"
                          ? "bg-brand-50 text-gray-700"
                          : p.status === "failed"
                            ? "bg-red-50 text-red-600"
                            : "bg-brand-50 text-gray-700"
                      }`}
                    >
                      {p.status === "paid"
                        ? "Pagado"
                        : p.status === "failed"
                          ? "Fallido"
                          : "Reembolsado"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TenantDetail;
